import { useEffect, useState } from 'react';
import { cn } from '@/lib/utils';
import { AreaChart, Area, ResponsiveContainer } from 'recharts';

function getRiskColor(score) {
  // Score is health score: 100=healthy, 0=critical. Higher is better.
  if (score >= 75) return { color: 'hsl(142, 72%, 45%)', label: 'Low Risk', badgeClass: 'bg-[hsl(142,72%,45%)]/15 text-[hsl(142,72%,45%)] border-[hsl(142,72%,45%)]/30', dotClass: 'bg-[hsl(142,72%,45%)]' };
  if (score >= 50) return { color: 'hsl(45, 93%, 58%)', label: 'Moderate Risk', badgeClass: 'bg-[hsl(45,93%,58%)]/15 text-[hsl(45,93%,58%)] border-[hsl(45,93%,58%)]/30', dotClass: 'bg-[hsl(45,93%,58%)]' };
  if (score >= 25) return { color: 'hsl(24, 94%, 56%)', label: 'High Risk', badgeClass: 'bg-[hsl(24,94%,56%)]/15 text-[hsl(24,94%,56%)] border-[hsl(24,94%,56%)]/30', dotClass: 'bg-[hsl(24,94%,56%)]' };
  return { color: 'hsl(0, 84%, 60%)', label: 'Critical', badgeClass: 'bg-[hsl(0,84%,60%)]/15 text-[hsl(0,84%,60%)] border-[hsl(0,84%,60%)]/30', dotClass: 'bg-[hsl(0,84%,60%)]' };
}

export function RiskScoreGauge({ score = 100, projectedScore, delta24h, trendData = [], className }) {
  const [animatedScore, setAnimatedScore] = useState(0);
  const risk = getRiskColor(score);

  useEffect(() => {
    const duration = 700;
    const startTime = Date.now();
    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setAnimatedScore(Math.round(eased * score));
      if (progress < 1) requestAnimationFrame(animate);
    };
    animate();
  }, [score]);

  const sparkData = trendData.slice(-15).map((d, i) => ({ i, v: d.score }));

  return (
    <div className={cn('', className)} data-testid="risk-score-gauge">
      <h3 className="text-base font-semibold mb-3">Enforcement Risk Score</h3>

      <span className={cn(
        'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border mb-4',
        risk.badgeClass
      )} data-testid="risk-score-label">
        <span className={cn('w-2 h-2 rounded-full', risk.dotClass)} />
        {risk.label}
      </span>

      <div className="flex items-end justify-between mb-2">
        <div>
          <div className="flex items-baseline gap-1">
            <p className="text-6xl font-semibold tracking-tight tabular-nums leading-none" data-testid="risk-score-value">
              {animatedScore}
            </p>
            <span className="text-2xl text-muted-foreground font-medium">/100</span>
          </div>
        </div>
        {sparkData.length > 2 && (
          <div className="w-[140px] h-[60px]">
            <ResponsiveContainer width="100%" height={60}>
              <AreaChart data={sparkData} margin={{ top: 2, right: 2, left: 2, bottom: 2 }}>
                <defs>
                  <linearGradient id="miniSparkGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={risk.color} stopOpacity={0.4} />
                    <stop offset="95%" stopColor={risk.color} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <Area type="monotone" dataKey="v" stroke={risk.color} strokeWidth={2} fill="url(#miniSparkGrad)" dot={false} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      {delta24h !== undefined && (
        <p className="text-sm text-muted-foreground mb-4">
          {delta24h === 0 ? (
            <>No change in the last 24 hours</>
          ) : (
            <>
              <span className="mr-1">{delta24h < 0 ? '↓' : '↑'}</span>
              <span className={delta24h < 0 ? 'text-[hsl(0,84%,60%)]' : 'text-[hsl(142,72%,45%)]'}>
                {delta24h > 0 ? '+' : ''}{delta24h}
              </span>
              {' '}points in the last 24 hours
            </>
          )}
        </p>
      )}

      {projectedScore !== undefined && projectedScore !== null && projectedScore !== score && (
        <>
          <div className="border-t border-border/60 my-4" />
          <div className="flex items-baseline gap-3">
            <span className="text-4xl font-semibold tabular-nums">{projectedScore}</span>
            <div>
              <span className="text-sm font-medium">Projected Score</span>
              <p className="text-xs text-muted-foreground">(If pending issues are not resolved)</p>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
